import { useState, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import { DropResult } from "@hello-pangea/dnd";
import { Project, projectsApi } from "../lib/api/projects";
import { Task, tasksApi } from "../lib/api/tasks";
import { CreateTaskModal } from "../components/tasks/CreateTaskModal";
import { TaskDetails } from "../components/tasks/TaskDetails";
import { ProjectSettings } from "../components/projects/ProjectSettings";
import {
  TaskFilters,
  type TaskFilters as TaskFiltersType,
} from "../components/tasks/TaskFilters";
import { ProjectHeader } from "../components/projects/ProjectHeader";
import { KanbanBoard } from "../components/tasks/KanbanBoard";
import { useFilteredTasks } from "../hooks/useFilteredTasks";

export default function ProjectDetails() {
  const { projectId } = useParams<{ projectId: string }>();
  const [project, setProject] = useState<Project | null>(null);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);
  const [showSettings, setShowSettings] = useState(false);
  const [filters, setFilters] = useState<TaskFiltersType>({});

  useEffect(() => {
    const fetchProjectAndTasks = async () => {
      if (!projectId) return;

      try {
        setLoading(true);
        setError(null);

        const [projectData, tasksData] = await Promise.all([
          projectsApi.get(projectId),
          tasksApi.list({ projectId }),
        ]);

        setProject(projectData);
        setTasks(tasksData.tasks);
      } catch (err) {
        setError(
          err instanceof Error ? err.message : "Failed to fetch project data",
        );
      } finally {
        setLoading(false);
      }
    };

    fetchProjectAndTasks();
  }, [projectId]);

  const handleDragEnd = useCallback(
    async (result: DropResult) => {
      if (!result.destination) return;

      try {
        const sourceIndex = result.source.index;
        const destinationIndex = result.destination.index;
        const status = result.destination.droppableId as Task["status"];

        const updatedTasks = [...tasks];
        const [movedTask] = updatedTasks.splice(sourceIndex, 1);
        const updatedTask = { ...movedTask, status };
        updatedTasks.splice(destinationIndex, 0, movedTask);

        // Optimistically update UI
        setTasks(
          updatedTasks.map((t) => (t._id === movedTask._id ? updatedTask : t)),
        );

        // Update task status in the backend
        await tasksApi.updateStatus(updatedTask._id, updatedTask.status);
      } catch (err) {
        // Revert optimistic update on error
        setError(
          err instanceof Error ? err.message : "Failed to update task status",
        );

        // Refresh tasks to ensure consistency
        if (projectId) {
          const { tasks: refreshedTasks } = await tasksApi.list({ projectId });
          setTasks(refreshedTasks);
        }
      }
    },
    [tasks, projectId],
  );

  const handleTaskCreated = useCallback((newTask: Task) => {
    setTasks((prevTasks) => [...prevTasks, newTask]);
  }, []);

  const { tasksByStatus } = useFilteredTasks(tasks, filters);

  if (loading) {
    return <div className="text-center py-8">Loading project...</div>;
  }

  if (error) {
    return <div className="text-red-500 text-center py-8">{error}</div>;
  }

  if (!project) {
    return <div className="text-center py-8">Project not found</div>;
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="space-y-6 mb-8">
        <ProjectHeader
          project={project}
          onCreateTask={() => setShowCreateModal(true)}
          onOpenSettings={() => setShowSettings(true)}
        />

        <TaskFilters
          tasks={tasks}
          filters={filters}
          onFilterChange={setFilters}
        />
      </div>

      <KanbanBoard
        tasksByStatus={tasksByStatus}
        onDragEnd={handleDragEnd}
        onTaskClick={setSelectedTask}
      />

      {showCreateModal && projectId && (
        <CreateTaskModal
          projectId={projectId}
          onClose={() => setShowCreateModal(false)}
          onTaskCreated={handleTaskCreated}
        />
      )}

      {selectedTask && (
        <TaskDetails
          task={selectedTask}
          onClose={() => setSelectedTask(null)}
          onTaskUpdated={(updatedTask) => {
            setTasks((prevTasks) =>
              prevTasks.map((task) =>
                task._id === updatedTask._id ? updatedTask : task,
              ),
            );
          }}
        />
      )}

      {showSettings && project && (
        <ProjectSettings
          project={project}
          onClose={() => setShowSettings(false)}
          onProjectUpdated={(updatedProject) => {
            setProject(updatedProject);
            setShowSettings(false);
          }}
        />
      )}
    </div>
  );
}
